import { rgba, lighten, darken, desaturate, saturate } from "polished";

import {
  fonts,
  font,
  fontFamilies,
  fontSizes,
  fontWeights,
  lineHeights,
  zIndices,
  space,
  sizes,
  radii,
  borderWidths,
  transition,
  breakpoints,
  mediaQueries,
  appScale,
  avatarScale,
  tagScale,
} from "./systemtheme.js";

// Dark theme: same spacing/font/size scales as systemtheme.
//
// Base palette (black, white, grey*, primary*, secondary*, success*, warning*,
// danger*) is IDENTICAL to the light theme. Only the semantic tokens
// (appBackground, surface, surfaceBorder, surfaceRaised, surfaceRaisedBorder,
// bodyText, subText, mutedText, ...) point to different base values per
// theme. Consumers should reference semantic tokens so the active theme
// resolves the correct colour automatically.

const black = "#000";
const white = "#fff";
const greyBackground = "#0d0d0d";
const grey = "#90979A"; // Base grey used for palette derivation
const primary = "#007ac4";
const secondary = "#8714f2";
const success = "#00883b";
const successDesaturated = desaturate(0.25, success);
const warning = "#FF8C00";
const warningSaturated = saturate(1.5, warning);
const danger = "#d90f27";

// Pre-compute greyscale palette once so semantic aliases below can reference
// the exact same values as the light theme (polished's darken/lighten math is
// deterministic on the same input).
const greyMid = lighten(0.15, grey);
const greyLight = lighten(0.2, grey);
const greyLighter = lighten(0.3, grey);
const greyLightest = lighten(0.35, grey);
const greyDark = darken(0.15, grey);
const greyDarker = darken(0.3, grey);
const greyDarkest = darken(0.5, grey);

const black90 = rgba(black, 0.9);
const black80 = rgba(black, 0.8);
const black70 = rgba(black, 0.7);
const black60 = rgba(black, 0.6);
const black50 = rgba(black, 0.5);
const black40 = rgba(black, 0.4);
const black30 = rgba(black, 0.3);
const black20 = rgba(black, 0.2);
const black10 = rgba(black, 0.1);

const white90 = rgba(white, 0.9);
const white80 = rgba(white, 0.8);
const white70 = rgba(white, 0.7);
const white60 = rgba(white, 0.6);
const white50 = rgba(white, 0.5);
const white40 = rgba(white, 0.4);
const white30 = rgba(white, 0.3);
const white20 = rgba(white, 0.2);
const white10 = rgba(white, 0.1);

const primaryLight = lighten(0.25, primary);
const primaryLighter = lighten(0.35, primary);
const primaryLightest = lighten(0.45, primary);
const primaryDark = darken(0.08, primary);
const primaryDarker = darken(0.16, primary);
const primaryDarkest = darken(0.24, primary);
const primary10 = rgba(primary, 0.1);
const primary20 = rgba(primary, 0.2);
const primary30 = rgba(primary, 0.3);
const primary40 = rgba(primary, 0.4);
const primary50 = rgba(primary, 0.5);
const primary60 = rgba(primary, 0.6);
const primary70 = rgba(primary, 0.7);
const primary80 = rgba(primary, 0.8);
const primary90 = rgba(primary, 0.9);

const secondaryLight = lighten(0.18, secondary);
const secondaryLighter = lighten(0.3, secondary);
const secondaryLightest = lighten(0.4, secondary);
const secondaryEvenLighter = lighten(0.45, secondary);
const secondaryDark = darken(0.08, secondary);
const secondaryDarker = darken(0.16, secondary);
const secondaryDarkest = darken(0.24, secondary);
const secondary10 = rgba(secondary, 0.1);
const secondary20 = rgba(secondary, 0.2);
const secondary30 = rgba(secondary, 0.3);
const secondary40 = rgba(secondary, 0.4);
const secondary50 = rgba(secondary, 0.5);
const secondary60 = rgba(secondary, 0.6);
const secondary70 = rgba(secondary, 0.7);
const secondary80 = rgba(secondary, 0.8);
const secondary90 = rgba(secondary, 0.9);

const successLight = lighten(0.22, successDesaturated);
const successLighter = lighten(0.35, successDesaturated);
const successLightest = lighten(0.45, successDesaturated);
const successEvenLighter = lighten(0.5, successDesaturated);
// Pale green card/surface fill — a subtle translucent tint so it reads on dark.
const successPale = rgba(success, 0.15);
const successDark = darken(0.08, success);
const successDarker = darken(0.16, success);
const successDarkest = darken(0.24, success);
const success10 = rgba(success, 0.1);
const success20 = rgba(success, 0.2);
const success30 = rgba(success, 0.3);
const success40 = rgba(success, 0.4);
const success50 = rgba(success, 0.5);
const success60 = rgba(success, 0.6);
const success70 = rgba(success, 0.7);
const success80 = rgba(success, 0.8);
const success90 = rgba(success, 0.9);

const warningLight = lighten(0.15, warning);
const warningLighter = lighten(0.25, warning);
const warningLightest = lighten(0.35, warning);
// Pale orange card/surface fill — a subtle translucent tint so it reads on dark.
const warningPale = rgba(warning, 0.15);
const warningDark = darken(0.08, warningSaturated);
const warningDarker = darken(0.16, warningSaturated);
const warningDarkest = darken(0.28, warningSaturated);
const warning10 = rgba(warning, 0.1);
const warning20 = rgba(warning, 0.2);
const warning30 = rgba(warning, 0.3);
const warning40 = rgba(warning, 0.4);
const warning50 = rgba(warning, 0.5);
const warning60 = rgba(warning, 0.6);
const warning70 = rgba(warning, 0.7);
const warning80 = rgba(warning, 0.8);
const warning90 = rgba(warning, 0.9);

const dangerLight = lighten(0.2, danger);
const dangerLighter = lighten(0.3, danger);
const dangerLightest = lighten(0.4, danger);
const dangerDark = darken(0.08, danger);
const dangerDarker = darken(0.18, danger);
const dangerDarkest = darken(0.26, danger);
const danger10 = rgba(danger, 0.1);
const danger20 = rgba(danger, 0.2);
const danger30 = rgba(danger, 0.3);
const danger40 = rgba(danger, 0.4);
const danger50 = rgba(danger, 0.5);
const danger60 = rgba(danger, 0.6);
const danger70 = rgba(danger, 0.7);
const danger80 = rgba(danger, 0.8);
const danger90 = rgba(danger, 0.9);

const appBackground = greyBackground;
const surface = greyDarkest;
const surfaceBorder = greyDarker;
const surfaceRaised = greyDarker;
const surfaceRaisedBorder = greyDark;
const hoverSurface = greyDark;
// Dark enough that the dimmed disabled text (grey) stays readable — greyDark
// was too light (grey-on-grey, e.g. the planner TARGET input was unreadable).
const disabledSurface = darken(0.42, grey);
// Table/planner rows must stay dark in dark mode so the inherited white body
// text keeps AA contrast. Header rows sit slightly above item rows (which sit
// just above `surface`) to preserve the light-theme hierarchy inverted.
const tableRowHeader = greyDarker; // header / group rows — elevated dark band
const tableRowItem = darken(0.42, grey); // sub-item rows — above surface, below header
const bodyText = white;
const subTextBody = greyLightest;
const subText = greyLighter;
const mutedText = grey;
const disabledText = grey;
const iconDefault = greyLighter;
const iconOnAccent = white;
const linkText = primaryLight;
// Selected side-nav item background — a saturated dark blue so the white
// active label/icon keeps AA contrast (the light-theme `primaryLightest`
// pill would leave white-on-pale-blue text unreadable on dark).
const navActiveBackground = primaryDark;
// AI chat / inline chart semantic tokens
const aiBrandGradientStart = "#0091ea";
const aiBrandGradientEnd = "#9b51e0";
const chartAxisLabel = "#e0e0e0";
const chartTooltipBackground = "#2a2a2a";
const chartSeriesDefault = "#60a5fa";
const tooltipBackground = surfaceRaised; // = greyDarker in dark mode
const tooltipText = bodyText; // = white in dark mode
const focusOutline = primary;
const dividerDefault = white20;
const dividerLight = white10;
const shadowColor = rgba(black, 0.6);
const buttonGroupTrack = darken(0.42, grey);
const buttonGroupSelected = greyDarker;
// primaryLighter, not linkText, keeps AA on the greyDarker selected segment.
const buttonGroupSelectedText = primaryLighter;

export const colors = {
  /* Greyscale palette — IDENTICAL to systemtheme. */
  black,
  black90,
  black80,
  black70,
  black60,
  black50,
  black40,
  black30,
  black20,
  black10,
  white,
  white90,
  white80,
  white70,
  white60,
  white50,
  white40,
  white30,
  white20,
  white10,
  grey,
  greyMid,
  greyLight,
  greyLighter,
  greyLightest,
  greyDark,
  greyDarker,
  greyDarkest,
  //
  // Semantic surface / foreground / icon / divider tokens.
  // ----------------------------------------------------
  // Each maps to a base palette value appropriate for the dark theme.
  //
  appBackground, // page bg
  surface, // card / sidenav / header / data containers
  surfaceBorder, // border around `surface`
  surfaceRaised, // modal / popover / select / input / menu bg
  surfaceRaisedBorder, // default border around surfaces and form inputs
  hoverSurface, // subtle tint on hover over a surface
  disabledSurface, // disabled form controls
  tableRowHeader, // header / group rows in tables and planners
  tableRowItem, // sub-item rows in tables and planners
  bodyText, // default text
  subTextBody, // secondary text (labels, captions, legends)
  subText, // secondary text (labels, captions)
  mutedText, // subtle / placeholder text
  disabledText, // disabled text / icons (same as light)
  iconDefault, // generic icon colour
  iconOnAccent, // icon on filled brand bg
  linkText, // default link foreground
  navActiveBackground, // selected side-nav item background
  aiBrandGradientStart,
  aiBrandGradientEnd,
  chartAxisLabel,
  chartTooltipBackground,
  chartSeriesDefault,
  tooltipBackground,
  tooltipText,
  focusOutline, // focus ring
  dividerDefault, // = white20
  dividerLight, // = white10
  shadowColor, // box-shadow colour (stronger than light so it remains visible on dark)
  buttonGroupTrack, // ButtonGroup track behind the segments
  buttonGroupSelected, // ButtonGroup selected segment
  buttonGroupSelectedText, // ButtonGroup selected segment text / icon
  /* Brand colour scales — identical to light theme base hues.
     Light variants are retuned (lighten more) so they remain legible as
     foreground text on a dark app bg, while base hues stay constant so
     white-on-fill contrast for buttons / chips matches the light theme. */
  primary,
  primaryLight,
  primaryLighter,
  primaryLightest,
  primaryDark,
  primaryDarker,
  primaryDarkest,
  primary10,
  primary20,
  primary30,
  primary40,
  primary50,
  primary60,
  primary70,
  primary80,
  primary90,
  secondary,
  secondaryLight,
  secondaryLighter,
  secondaryLightest,
  secondaryEvenLighter,
  secondaryDark,
  secondaryDarker,
  secondaryDarkest,
  secondary10,
  secondary20,
  secondary30,
  secondary40,
  secondary50,
  secondary60,
  secondary70,
  secondary80,
  secondary90,
  success,
  successLight,
  successLighter,
  successLightest,
  successEvenLighter,
  successPale,
  successDark,
  successDarker,
  successDarkest,
  success10,
  success20,
  success30,
  success40,
  success50,
  success60,
  success70,
  success80,
  success90,
  warning,
  warningLight,
  warningLighter,
  warningLightest,
  warningPale,
  warningDark,
  warningDarker,
  warningDarkest,
  warning10,
  warning20,
  warning30,
  warning40,
  warning50,
  warning60,
  warning70,
  warning80,
  warning90,
  danger,
  dangerLight,
  dangerLighter,
  dangerLightest,
  dangerDark,
  dangerDarker,
  dangerDarkest,
  danger10,
  danger20,
  danger30,
  danger40,
  danger50,
  danger60,
  danger70,
  danger80,
  danger90,
};

// Shadows: stronger on dark so they remain visible against dark surfaces.
export const shadows = {
  boxDefault: `0 2px 5px 0 ${colors.shadowColor}`,
  thinOutline: `0 0 0 1px`,
  thickOutline: `0 0 0 3px`,
};

// GRADIENTS — dark-theme values for the same tokens defined in systemtheme.js.
// Tuned so the pink/purple accent still reads on a dark surface and the
// canvas title read-through fades to the dark appBackground.
export const gradients = {
  // AI Summary panel: keep the subtle pink/purple tint but darken the base
  // so it sits naturally on a dark surface rather than reading as white.
  aiSummarySurface:
    "linear-gradient(to right, rgb(58, 40, 58), rgb(50, 50, 70))",
  aiSummaryHover: "linear-gradient(to right, rgb(74, 52, 74), rgb(62, 62, 86))",
  // Same brand-accent border; keeps the visual identity across both themes.
  aiSummaryBorder:
    "linear-gradient(to right, rgb(203, 83, 203), rgb(114, 114, 221))",
  // Brand accent gradient — same as light theme.
  aiBrand: "linear-gradient(135deg, #0091ea 0%, #9b51e0 100%)",
  // Radial read-through fading to the dark app background so nodes on
  // dark canvas remain visible behind the title.
  canvasTitleReadthrough: `radial-gradient(${colors.surface} 0%, ${rgba(colors.surface, 0.8)} 50%, ${rgba(colors.surface, 0)} 100%)`,
};

export {
  fonts,
  font,
  fontFamilies,
  fontSizes,
  fontWeights,
  lineHeights,
  zIndices,
  space,
  sizes,
  radii,
  borderWidths,
  transition,
  breakpoints,
  mediaQueries,
  appScale,
  avatarScale,
  tagScale,
};

export default {
  name: "dark",
  isDark: true,
  fonts,
  font,
  fontFamilies,
  fontSizes,
  fontWeights,
  lineHeights,
  radii,
  borderWidths,
  space,
  sizes,
  shadows,
  colors,
  gradients,
  breakpoints,
  transition,
  zIndices,
  appScale,
  avatarScale,
  tagScale,
};
